import { useLazyQuery } from '@apollo/client/react';
import { useCallback } from 'react';

import { DialogID, useDialog } from '@components/Dialog';
import { AddressInput, AnchoredServiceType } from '@generated/gql/graphql';
import { useSyncState } from '@hooks/useSyncState';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  SignupAddressAlreadySuppliedEventContext,
  useTrackSignupAddressAlreadySupplied,
} from '@tracking/trackingEvents';
import { anchoredServicesQuery } from '@utils/queries/address';

const mapToSignupAddressContext = (
  address: AddressInput
): SignupAddressAlreadySuppliedEventContext => ({
  organisation: address.organisation ?? '',
  department: address.department ?? '',
  sub_building: address.subBuilding ?? '',
  building_name: address.buildingName ?? '',
  building_number: address.buildingNumber ?? '',
  dependent_thoroughfare: address.dependentThoroughfare ?? '',
  thoroughfare: address.thoroughfare ?? '',
  double_dependent_locality: address.doubleDependentLocality ?? '',
  dependent_locality: address.dependentLocality ?? '',
  post_town: address.postTown ?? '',
  postcode: address.postcode,
  uprn: address.uprn ?? '',
});

export const useAnchoredServices = () => {
  const dialog = useDialog(DialogID.ADDRESS_ALREADY_SUPPLIED);
  const trackAddressAlreadySupplied = useTrackSignupAddressAlreadySupplied();
  const { trackingContexts } = useTrackingContext();

  const [suppliedServices, setSuppliedServices] = useSyncState<
    { type: AnchoredServiceType }[]
  >('address.suppliedServices', []);
  const [fetch] = useLazyQuery(anchoredServicesQuery);

  const checkAddressSupply = useCallback(
    (address: AddressInput, onNotSupplied: () => void) => {
      const promise = fetch({ variables: { address } }).catch(() => null);
      return async () => {
        const result = await promise;
        const services = result?.data?.anchoredServices?.services ?? [];
        if (services.length > 0) {
          setSuppliedServices(services);
          dialog.setOpen(true);

          trackAddressAlreadySupplied(mapToSignupAddressContext(address), {
            contexts: trackingContexts,
          });
        } else {
          onNotSupplied();
        }
      };
    },
    [
      fetch,
      dialog,
      setSuppliedServices,
      trackAddressAlreadySupplied,
      trackingContexts,
    ]
  );

  return { checkAddressSupply, suppliedServices };
};
